import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Context } from "../../models/context";
import { AnnouncementI, DeleteAnnouncement, GetAllAnnouncements, PostAnnouncement } from "../../services/announcements";
import { GetGroups, GroupI } from "../../services/groups";
import "./announcement.css";
import Help from "../../components/help/Help";
import { useAlerts } from "../../contexts/AlertContext";

const APP_NAME_OPTIONS = [
    { label: "quickhints", value: "quickhints" },
    { label: "geohints", value: "geohints" },
    { label: "All Apps", value: "all" },
];

function toIsoString(localDatetime: string): string {
    if (!localDatetime) return "";
    const d = new Date(localDatetime);
    return d.toISOString().replace(/(\.\d{3})Z$/, "$1Z");
}

export default function Announcement() {
    const context = useContext(Context);
    const navigate = useNavigate();

    const [appName, setAppName] = useState(APP_NAME_OPTIONS[0].value);
    const [groupId, setGroupId] = useState("all");
    const [groups, setGroups] = useState<GroupI[]>([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [startTime, setStartTime] = useState("");
    const [expirationTime, setExpirationTime] = useState("");

    const [announcements, setAnnouncements] = useState<AnnouncementI[]>([]);
    const [filterGroupId, setFilterGroupId] = useState("");
    const [filterAppName, setFilterAppName] = useState("");
    const [listLoading, setListLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    const {addError, addAlert} = useAlerts();

    useEffect(() => {
        if (!context.user) {
            navigate("/login?entryPoint=announcement");
            return;
        }
        fetchGroups();
    }, [context.user, navigate]);

    useEffect(() => {
        if (context.user) fetchAnnouncements();
    }, [context.user]);

    async function fetchGroups() {
        if (!context.user) return;
        setGroupsLoading(true);
        try {
            const result = await new GetGroups({ count: 100 }).request(context.user);
            setGroups(result);
        } catch(error) {
            setGroups([]);
            addError(error as Error, "Error loading groups")
        } finally {
            setGroupsLoading(false);
        }
    }

    async function fetchAnnouncements() {
        if (!context.user) return;
        setListLoading(true);
        try {
            const result = await new GetAllAnnouncements().request(context.user);
            setAnnouncements(result);
        } catch (e: any) {
            addError(e as Error, "Error loading announcements")
            setAnnouncements([]);
        } finally {
            setListLoading(false);
        }
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!context.user) return;
        setSubmitLoading(true);

        const payload: AnnouncementI = {
            title,
            message,
            ...(startTime && { startTime: toIsoString(startTime) }),
            ...(expirationTime && { expirationTime: toIsoString(expirationTime) }),
        };

        try {
            await new PostAnnouncement(appName, groupId).request(payload, context.user);
            addAlert("Announcement posted.",5000,'success',true);
            setTitle("");
            setMessage("");
            setStartTime("");
            setExpirationTime("");
            await fetchAnnouncements();
        } catch (e: any) {
            addError(e as Error,"Failed to post announcement");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDelete = async (a: AnnouncementI) => {
        if (!context.user || !a.announcementId) return;
        try {
            await new DeleteAnnouncement(a.announcementId, a.appName ?? appName, a.groupId ?? groupId).request(context.user);
            await fetchAnnouncements();
        } catch (e: any) {
            addError(e as Error, "Failed to delete announcement");
        }
    };

    return (

        <div id="announcement-page">
            <div id="announcement-columns">
                <div id="post-announcements">

                    <div id="announcement-wrapper" className="container">
                        <div className="help-title">
                            <h1>Post Announcement</h1>
                            <Help title="Post Announcement" color="var(--green-medium)" className="help-button">
                                <p>Fill out the form to post an announcement. After filling it out, click the "Post" button to publish it.</p>
                                <h3>Announcement Details</h3>
                                <div id="announcement details">
                                    <p id="announcements-details-item"><b>App Name:</b> Use the dropdown to select the app you wish to post for. Choose "All Apps" to broadcast to every app.</p>
                                    <p id="announcements-details-item"><b>Group ID:</b> Use the dropdown to select the group you wish the announcement to go to. Choose "All Groups" to broadcast to every group.</p>
                                    <p id="announcements-details-item"><b>Title:</b> Give your announcement a title</p>
                                    <p id="announcements-details-item"><b>Message:</b> Write your announcement message </p>
                                    <p id="announcements-details-item"><b>Start/End Time (Optional):</b> Choose a day for your announcement to be posted,
                                    and choose an expiration date</p>
                                </div>
                            </Help>
                        </div>
                        <form id="announcement-form" onSubmit={handleSubmit}>
                            <ul id="announcement-form-body">
                                <li>
                                    <label htmlFor="app-name" id="app-name-label">App Name: </label>
                                    <select id="app-name" value={appName} onChange={e => setAppName(e.target.value)}>
                                        {APP_NAME_OPTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                    </select>
                                </li>
                                <li>
                                    <label htmlFor="group-id" id="group-id-label">Group ID: </label>
                                    <select id="group-id" value={groupId} onChange={e => setGroupId(e.target.value)} disabled={groupsLoading}>
                                        <option value="all">All Groups</option>
                                        {groupsLoading && <option value="" disabled>Loading...</option>}
                                        {groups.map(g => <option key={g.groupId} value={g.groupId}>{g.groupName}</option>)}
                                    </select>
                                </li>
                                <li>
                                    <label htmlFor="title" id="title-label">Title: </label>
                                    <input
                                        type="text"
                                        id="title-id"
                                        name="title"
                                        placeholder="Title"
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        required
                                    />
                                </li>
                                <li>
                                    <label htmlFor="message" id="message-label">Message: </label>
                                    <textarea
                                        id="message-id"
                                        name="message"
                                        placeholder="Announcement message"
                                        value={message}
                                        onChange={e => setMessage(e.target.value)}
                                        required
                                    />
                                </li>
                                <li>
                                    <label htmlFor="start-time" id="start-time-label">Start Time (optional): </label>
                                    <input
                                        type="datetime-local"
                                        id="start-time"
                                        value={startTime}
                                        onChange={e => setStartTime(e.target.value)}
                                    />
                                </li>
                                <li>
                                    <label htmlFor="expiration-time" id="expiration-time-label">Expiration Time (optional): </label>
                                    <input
                                        type="datetime-local"
                                        id="expiration-time"
                                        value={expirationTime}
                                        onChange={e => setExpirationTime(e.target.value)}
                                    />
                                </li>
                            </ul>
                            <div id='announcement-actions'>
                            <input
                                type="submit"
                                id="submit-announcement"
                                className="button"
                                value={submitLoading ? "Posting..." : "Post"}
                                disabled={submitLoading}
                            />
                            </div>
                        </form>
                    </div>



                </div>
                {/* TODO: fix active announcements and style it */}

                <div id="announcement-list-wrapper" className="container">
                    <div className="help-title">
                        <h1>Active Announcements</h1>
                        <Help title="Active Announcements" color="var(--green-medium)" className="help-button">
                            <p>All active announcements are shown here. Use the group filter to narrow by group.</p>
                        </Help>
                    </div>

                    <div id="announcement-list-filter">
                        <label htmlFor="filter-app-name">App:</label>
                        <select
                            id="filter-app-name"
                            value={filterAppName}
                            onChange={e => setFilterAppName(e.target.value)}
                        >
                            <option value="">All</option>
                            {APP_NAME_OPTIONS.filter(a => a.value !== "all").map(a => (
                                <option key={a.value} value={a.value}>{a.label}</option>
                            ))}
                        </select>
                        <label htmlFor="filter-group-id">Group:</label>
                        <select
                            id="filter-group-id"
                            value={filterGroupId}
                            onChange={e => setFilterGroupId(e.target.value)}
                            disabled={groupsLoading}
                        >
                            <option value="">All</option>
                            <option value="all">Broadcasts</option>
                            {groups.map(g => <option key={g.groupId} value={g.groupId}>{g.groupName}</option>)}
                        </select>
                    </div>

                    {listLoading && <p className="announcement-loading">Loading...</p>}
                    {!listLoading && announcements.filter(a =>
                        (!filterAppName || a.appName === filterAppName) &&
                        (!filterGroupId || a.groupId === filterGroupId)
                    ).length === 0 && (
                        <p className="announcement-empty">No active announcements.</p>
                    )}
                    <ul id="announcement-list">
                        {announcements.filter(a =>
                            (!filterAppName || a.appName === filterAppName) &&
                            (!filterGroupId || a.groupId === filterGroupId)
                        ).map(a => (
                            <li key={a.announcementId} className="announcement-card">
                                <div className="announcement-card-header">
                                    <strong className="announcement-card-title">{a.title}</strong>
                                    <button
                                        type="button"
                                        className="announcement-delete-btn"
                                        onClick={() => handleDelete(a)}
                                    >
                                        Delete
                                    </button>
                                </div>
                                <p className="announcement-card-message">{a.message}</p>
{a.startTime && <span className="announcement-card-meta">Starts: {new Date(a.startTime).toLocaleString()}</span>}
                                {a.expirationTime && <span className="announcement-card-meta">Expires: {new Date(a.expirationTime).toLocaleString()}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}
